import React, { useEffect, useRef, useState } from 'react';
import { range } from 'lodash';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { List, Stack } from '@mui/material';

import { Typography } from '../../Typography';
import { Button2 } from '../../Button2';
import { MonthYearData } from '../types';
import { RedDot } from '../RedDot';


interface ValueList {
    type: 'months' | 'years';
    setSelectedDate: React.Dispatch<React.SetStateAction<MonthYearData>>;
}


const MONTHS_COUNT = 12;
const MIN_YEAR = 2023;
const MAX_YEAR = 2124;


const getStackValues = (type: ValueList['type']) => {
    if (type === 'months') {
        return range(MONTHS_COUNT).map(item => {
            const month = format(new Date(MIN_YEAR, item), 'LLLL', { locale: ru });

            return {
                name: month[0].toUpperCase() + month.substring(1),
                value: item,
            };
        });
    }

    return range(MIN_YEAR, MAX_YEAR).map(item => ({
        name: String(item),
        value: item,
    }));
};


export const ValueDateList: React.FC<ValueList> = ({ type, setSelectedDate }) => {
    const scrollRef = useRef(null);

    const [pressedValue, setPressedValue] = useState<number>(null);

    useEffect(() => {
        if (scrollRef)
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [scrollRef]);

    useEffect(() => {
        setSelectedDate(({ monthSelected, yearSelected, ...rest }) => ({
            ...rest,
            monthSelected: false,
            yearSelected: false,
        }));
    }, [setSelectedDate]);

    const isMonths = type === 'months';
    const title = isMonths ? 'Месяц' : 'Год';
    const focusValue = isMonths ? new Date().getMonth() : new Date().getFullYear();
    const stackValues = getStackValues(type);

    return (
        <Stack width='50%'>
            <Typography
                variant='regularCaption3'
                alignSelf='center'
                sx={{
                    color: 'grayscale.shade85',
                }}
            >
                {title}
            </Typography>

            <Stack
                mt={2}
                width='100%'
                alignItems='center'
                sx={({ spacing }) => ({
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        zIndex: 1,
                        left: spacing(2),
                        right: spacing(2),
                        height: '15%',
                        top: spacing(17),
                        background: 'linear-gradient(360deg, rgba(139,167,32,0) 0%, rgba(255,255,255,1) 100%)',
                        pointerEvents: 'none',
                    },
                    '&:after': {
                        content: '""',
                        position: 'absolute',
                        left: spacing(2),
                        right: spacing(2),
                        height: '15%',
                        bottom: spacing(0.5),
                        background: 'linear-gradient(180deg, rgba(139,167,32,0) 0%, rgba(255,255,255,1) 100%)',
                        pointerEvents: 'none',
                    },
                })}
            >
                <List
                    sx={{
                        width: '100%',
                        overflow: 'auto',
                        maxHeight: ({ spacing }) => spacing(41),
                        '& ul': { padding: 0 },

                        '::-webkit-scrollbar': {
                            display: 'none',
                        },
                    }}
                >
                    {stackValues.map(({ name, value }) => (
                        <li
                            key={name}
                            {...value === focusValue && {
                                ref: ref => {
                                    scrollRef.current = ref;
                                },
                            }}
                            style={{ scrollMargin: '25px' }}
                        >
                            <ul>
                                <Button2
                                    onClick={() => {
                                        setSelectedDate(({ currentDate, ...rest }) => ({
                                            ...rest,
                                            currentDate: isMonths
                                                ? new Date(currentDate.setMonth(value))
                                                : new Date(currentDate.setFullYear(value)),
                                            ...isMonths
                                                ? { monthSelected: true }
                                                : { yearSelected: true },
                                        }));

                                        setPressedValue(value);
                                    }}
                                    variant='secondary'
                                    size='small'

                                    sx={{
                                        width: '100%',

                                        ...pressedValue === value && {
                                            backgroundColor: 'general.primary50',
                                            color: 'grayscale.shade0',
                                        },

                                        '&:hover': {
                                            backgroundColor: 'grayscale.shade10',
                                        },

                                        '&:focus': {
                                            backgroundColor: pressedValue === value ? 'general.primary50' : 'transparent',
                                            color: pressedValue === value ? 'grayscale.shade0' : 'grayscale.shade100',

                                            ...pressedValue !== value && {
                                                '&:hover': {
                                                    backgroundColor: 'grayscale.shade10',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <Typography
                                        variant='regularBody2'
                                    >
                                        {name}
                                        {value === focusValue &&
                                        <RedDot condition={pressedValue === value} />}
                                    </Typography>
                                </Button2>
                            </ul>
                        </li>
                    ))}
                </List>
            </Stack>
        </Stack>
    );
};
